import React, {ChangeEvent, FC, FormEvent, useEffect, useState} from "react";
import {useNavigate} from "react-router";
import {useParams} from "react-router";
import Breadcrumb, {Crumb} from "../../../../components/common/Breadcrumbs";
import {useGetEnumsQuery} from "../../../../app/globalApi";
import AuditableInfoButton from "../../../../components/common/form/AuditableInfoButton";
import Form from "../../../../components/common/form/Form";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import ButtonTray from "../../../../components/common/form/ButtonTray";
import {
    useCreateNoteLogicMutation,
    useDeleteNoteByIdMutation,
    useGetNoteByIdQuery,
    useUpdateNoteMutation
} from "../noteApi";
import {useLazyGetCustomerByTenantAndLevelQuery} from "../../../customer/customerApi";
import {handleRTKQuery} from "../../../../helpers/utils/RtkQueryUtils";
import {Routes as AppRoutes} from "../../../../routes";
import {createOptionListForSelectTag} from "../../../../helpers/utils/Utils";
import {hasPermission} from "../../../../helpers/utils/AccessControlUtils";
import {useGetCodesListQuery} from "../../../sysAdmin/code/codeApi";
import {useGetAllTenantsQuery} from "../../../customer/tenantApi";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";
import {FaTrash} from "react-icons/fa6";
import ModalButton from "../../../../components/common/form/ModalButton";
import IconButton from "../../../../components/common/form/IconButton";
import {skipToken} from "@reduxjs/toolkit/query";

interface Props {
    crumbs: Crumb[];
    title: string;
    btnText: string;
    isEditable?: boolean;
}

const NoteLogicFormField: FC<Props> = ({crumbs, title, btnText, isEditable = true}) => {

    const navigate = useNavigate();
    let {noteLogicId} = useParams();
    const {data: enums} = useGetEnumsQuery("");
    const {data: codeList} = useGetCodesListQuery("");
    const {data: tenants} = useGetAllTenantsQuery("");
    const {data: noteData} = useGetNoteByIdQuery(noteLogicId ?? skipToken);
    const [noteLogicData, setNoteLogicData] = useState<any>({})
    const [updateNoteLogicApi, updateNoteLogicApiResponse] = useUpdateNoteMutation();
    const [customerDataApi, customerDataApiResponse] = useLazyGetCustomerByTenantAndLevelQuery();
    const [createNoteLogic, createNoteLogicApiResponse] = useCreateNoteLogicMutation();
    const [deleteApi, {isLoading}] = useDeleteNoteByIdMutation();

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const updatedData = {
            ...noteLogicData,
            type: noteLogicData.type?.value,
            tenant: noteLogicData.tenant?.value,
            customerId: noteLogicData.customer?.value,
            code: noteLogicData.code ? {id: noteLogicData.code.value} : null, // **FIXED: Send `code` as object**
            productTypes: noteLogicData.productTypes?.map((pt: { value: any }) => pt.value),
        };

        const createOrUpdateBillRelease = async () => {
            if (btnText !== "Update") {
                return await createNoteLogic(updatedData).unwrap();
            } else {
                return await updateNoteLogicApi({
                    id: noteLogicId,
                    requestBody: updatedData,
                }).unwrap();
            }
        };
        await handleRTKQuery(createOrUpdateBillRelease, (response: any) => {
             if (response?.success || response?.data?.success) {
                setNoteLogicData({});
                navigate(AppRoutes.NoteLogics.path);
            }
        });
    };
    useEffect(() => {
        if (noteData) {
            setNoteLogicData({
                ...noteData,
                type: noteData.type ? {value: noteData.type, label: noteData.type} : null,
                code: noteData.code ? {value: noteData.code.id, label: noteData.code.name} : null,
                productTypes: noteData.productTypes?.map((pt: any) => ({value: pt, label: pt})) || [],
                tenant: noteData.tenant ? {value: noteData.tenant, label: noteData.tenant} : null,
                customer: noteData.customerId
                    ? {value: noteData.customerId, label: "Loading..."} // Placeholder until fetched
                    : null,
            });

            if (noteData.customerId) {
                customerDataApi({tenantName: noteData.tenant, level: "CUSTOMER"}).then((res) => {
                    const customer = res.data?.find((c: any) => c.id === noteData.customerId);
                    if (customer) {
                        setNoteLogicData((prev: any) => ({
                            ...prev,
                            customer: {value: customer.id, label: customer.customerName},
                        }));
                    }
                });
            }

            if (noteData.tenant) {
                customerDataApi({tenantName: noteData.tenant, level: "CUSTOMER"});
            }
        }
    }, [noteData]);

    const handleOnChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { name: string; value: any }) => {
        const {name, value} = "target" in e ? e.target : e;
        setNoteLogicData((prev: any) => ({...prev, [name]: value}));

        if (name === "tenant" && value) {
            customerDataApi({tenantName: value.value, level: "CUSTOMER"});
        }
    };

    const handleDelete = async () => {
        if (noteLogicId) {
            await deleteApi(noteLogicId).unwrap();
        }
        navigate(AppRoutes.NoteLogics.path)
    };
    return (
        <div className="px-4 py-2">

            <div className={"justify-between sm:block md:flex lg:flex"}>
                <Breadcrumb crumbs={crumbs}/>
                <div className={"justify-between sm:block md:flex lg:flex"}>
                    {noteLogicId && (
                        <>
                            <ModalButton
                                triggerButton={
                                    <IconButton
                                        icon={<FaTrash/>}
                                        title="Delete Note Logic"
                                        hasBackground={true}
                                        className="mr-2"
                                    />
                                }
                                modal={
                                    <ConfirmationModal
                                        show={true}
                                        setShow={() => {
                                        }}
                                        title="Delete Note Logic"
                                        bodyMessage="Are you sure you want to delete this Note Logic?"
                                        onSubmit={() => handleDelete()}
                                        isLoading={isLoading}
                                    />
                                }
                            />
                            { hasPermission( ["AUDIT-V"]) &&
                                <AuditableInfoButton obj={noteData}/>
                            }
                        </>
                    )}

                </div>
            </div>


            <div className="flex justify-center mt-3">
                <div className="bg-text-0 lg:w-4/6 sm:w-full p-5 rounded-md shadow-[0_0_10px_-2px_rgba(0,0,0,0.2)]">
                    <h3 className="text-xl font-semibold text-surface-0 pb-3 border-b border-surface-7">
                        {title}
                    </h3>


                    <Form onSubmit={handleSubmit} fields={
                        <>
                            <InputField
                                name="name"
                                label="Name"
                                onChange={handleOnChange}
                                value={noteLogicData?.name || ""}
                                required
                                placeholder="Enter Name..."
                            />

                            <SelectField
                                label="Code"
                                value={noteLogicData?.code}
                                options={createOptionListForSelectTag(codeList, "name", "id")}
                                onChange={(option) => handleOnChange({name: "code", value: option})}
                                required
                            />
                            <SelectField
                                label="Tenant"
                                value={noteLogicData?.tenant}
                                options={createOptionListForSelectTag(tenants, "name", "name")}
                                onChange={(option) => handleOnChange({name: "tenant", value: option})}
                                required
                            />
                            <SelectField
                                label="Customer"
                                value={noteLogicData?.customer}  // Ensure customer value is pre-populated correctly
                                options={createOptionListForSelectTag(customerDataApiResponse.data, "customerName", "id")}
                                onChange={(option) => handleOnChange({name: "customer", value: option})}
                            />

                            <SelectField
                                label="Product Type"
                                value={noteLogicData?.productTypes}
                                options={createOptionListForSelectTag(enums?.["ProductType"], "value", "constant")}
                                onChange={(option) => handleOnChange({name: "productTypes", value: option})}
                                isMulti
                                required
                            />

                            <TextAreaField
                                label="Message"
                                name="message"
                                value={noteLogicData?.message || ""}
                                onChange={handleOnChange}
                                required
                                placeholder="Enter description..."
                            />
                            <InputField
                                name="bold"
                                label="Bold"
                                type="checkbox"
                                checked={noteLogicData.bold}
                                onChange={() =>
                                    setNoteLogicData((prev: { bold: any }) => ({
                                        ...prev,
                                        bold: !prev.bold,
                                    }))
                                }
                            />
                            <InputField
                                name="redColor"
                                label="Red Color"
                                type="checkbox"
                                checked={noteLogicData.redColor}
                                onChange={() =>
                                    setNoteLogicData((prev: { redColor: any }) => ({
                                        ...prev,
                                        redColor: !prev.redColor,
                                    }))
                                }
                            />
                            <InputField
                                name="yellowHighlight"
                                label="Yellow Highlight"
                                type="checkbox"
                                checked={noteLogicData.yellowHighlight}
                                onChange={() =>
                                    setNoteLogicData((prev: { yellowHighlight: any }) => ({
                                        ...prev,
                                        yellowHighlight: !prev.yellowHighlight,
                                    }))
                                }
                            />
                            {btnText !== "Create" && (
                                <InputField
                                    name="enabled"
                                    label="Is Enabled"
                                    type="checkbox"
                                    checked={noteLogicData.enabled}
                                    onChange={() =>
                                        setNoteLogicData((prev: { enabled: any }) => ({
                                            ...prev,
                                            enabled: !prev.enabled,
                                        }))
                                    }
                                />
                            )}

                            <div className="col-span-12 md:grid md:grid-cols-12 gap-3">
                                <div className="col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center">
                                </div>
                                <div className="relative col-span-9">
                                    <div className={"mt-4 flex"}>

                                        <ButtonTray buttons={[
                                            {
                                                buttonProps: {
                                                    btnText: "Cancel",
                                                    onClick: () => navigate(AppRoutes.NoteLogics.path),
                                                    type: "cancel"
                                                },
                                                buttonType: "button"
                                            },
                                            {
                                                buttonProps: {
                                                    btnText: "Submit",
                                                    type: "submit",
                                                    isLoading: (createNoteLogicApiResponse.isLoading || updateNoteLogicApiResponse.isLoading),
                                                    isVisible: isEditable
                                                },
                                                buttonType: "button"
                                            }
                                        ]} align="center" gap={2}/>
                                    </div>
                                </div>
                            </div>

                        </>
                    }/>
                </div>
            </div>
        </div>
    );
};


export default NoteLogicFormField;
